/*
 * Cart discount code form.
 *
 * Applies and removes discount codes through /cart/update.js, then re-renders
 * the applied-code pills and the savings line through the Section Rendering
 * API (section `api-cart-discount-form`).
 *
 * The cart API only says whether a code is `applicable`, never why. To tell a
 * customer something more useful than "invalid", a rejected code is tested on
 * its own when another code is already applied: if it works alone, the real
 * problem is that the two codes do not combine. The cart is always put back to
 * the codes it had before.
 *
 * Storefront strings come from data attributes on the element, rendered from
 * the locale files by snippets/cart-discount-form.liquid.
 */
class CartDiscountForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.input = this.querySelector('input[name="discount"]');
    this.button = this.querySelector('[data-apply-discount]');
    this.error = this.querySelector('[data-discount-error]');

    // Delegated on the host so re-rendered pills never need re-binding.
    this.addEventListener('submit', this.onSubmit.bind(this));
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-remove-discount]')) this.removeDiscount(e);
    });

    // Quantity changes and removals change the savings amount too.
    this.onCartChange = () => {
      if (this.busy) return;
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refreshSection(), 300);
    };
    document.addEventListener('theme:cart:change', this.onCartChange);
  }

  disconnectedCallback() {
    document.removeEventListener('theme:cart:change', this.onCartChange);
    clearTimeout(this.refreshTimer);
    this.initialized = false;
  }

  string(name, fallback) {
    return this.dataset[name] || fallback;
  }

  appliedCodes() {
    return Array.from(this.querySelectorAll('.cart-discount__pill'))
      .map((pill) => pill.dataset.discountCode)
      .filter(Boolean);
  }

  async updateDiscounts(codes) {
    const resp = await fetch(`${window.theme.routes.cart}/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    if (!resp.ok) throw new Error(`Cart update failed: ${resp.status}`);
    return resp.json();
  }

  isApplicable(cart, code) {
    const match = (cart.discount_codes || []).find(
      (d) => d.code && d.code.toLowerCase() === code.toLowerCase()
    );
    return Boolean(match && match.applicable);
  }

  async onSubmit(e) {
    if (!e.target.closest('form')) return;
    e.preventDefault();
    if (this.busy) return;

    const code = this.input.value.trim();
    this.hideError();

    if (!code) {
      this.showError(this.string('errorEmpty', 'Please enter a code'));
      return;
    }

    const previous = this.appliedCodes();

    if (previous.some((c) => c.toLowerCase() === code.toLowerCase())) {
      this.showError(this.string('errorAlreadyApplied', 'This code is already applied.'));
      return;
    }

    this.setBusy(true);

    try {
      const cart = await this.updateDiscounts(previous.concat(code));
      const allApplicable = previous.concat(code).every((c) => this.isApplicable(cart, c));

      if (allApplicable) {
        this.input.value = '';
        this.announceCartChange(cart);
        await this.refreshSection();
        return;
      }

      let message = this.string('errorInvalid', 'Invalid discount code');

      if (previous.length > 0) {
        // Does the new code work on its own? Then it only clashes with the one already applied.
        const alone = await this.updateDiscounts([code]);
        if (this.isApplicable(alone, code)) {
          message = this.string('errorNotCombinable', 'Discount codes cannot be combined on this order.');
        }
      }

      // Put the cart back exactly as it was, without the rejected code.
      const restored = await this.updateDiscounts(previous);
      this.announceCartChange(restored);
      await this.refreshSection();
      this.showError(message);
    } catch (err) {
      console.error(err);
      this.showError(this.string('errorNetwork', 'Something went wrong. Please try again.'));
      this.refreshSection();
    } finally {
      this.setBusy(false);
    }
  }

  async removeDiscount(e) {
    const pill = e.target.closest('.cart-discount__pill');
    if (!pill || this.busy) return;

    const codes = this.appliedCodes().filter((c) => c !== pill.dataset.discountCode);
    this.setBusy(true);

    try {
      const cart = await this.updateDiscounts(codes);
      this.hideError();
      this.announceCartChange(cart);
      await this.refreshSection();
    } catch (err) {
      console.error(err);
      this.showError(this.string('errorNetwork', 'Something went wrong. Please try again.'));
    } finally {
      this.setBusy(false);
    }
  }

  announceCartChange(cart) {
    document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart }, bubbles: true }));
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
  }

  setBusy(busy) {
    this.busy = busy;
    if (this.button) this.button.disabled = busy;
  }

  showError(msg) {
    if (!this.error) return;
    const small = this.error.querySelector('small');
    if (small && msg) small.textContent = msg;
    this.error.classList.remove('hide');
  }

  hideError() {
    this.error?.classList.add('hide');
  }

  // Re-renders the applied-code pills and the savings line.
  async refreshSection() {
    try {
      const resp = await fetch(`${window.theme.routes.root_url}?sections=api-cart-discount-form`);
      if (!resp.ok) return;

      const data = await resp.json();
      const markup = data['api-cart-discount-form'];
      if (!markup) return;

      const html = new DOMParser().parseFromString(markup, 'text/html');
      const updated = html.querySelector('[data-discount-codes]');
      const current = this.querySelector('[data-discount-codes]');

      if (updated && current) current.replaceWith(updated);
    } catch (error) {
      console.error('Error refreshing discount section:', error);
    }
  }
}

if (!customElements.get('cart-discount-form')) {
  customElements.define('cart-discount-form', CartDiscountForm);
}
